import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { Injectable, inject, OnDestroy } from '@angular/core'

import { ApiRiskScorecardService } from '@data/services/modules/risk-scorecard/api-risk-scorecard.service'
import { SnapshotContext, SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { RiskScorecardView } from '@data/interfaces/risk-scorecard.interface'

/**
 * La respuesta de la calculadora, compartida por las pestañas Flujos, Estados financieros, Ratios y Calculadora: una
 * sola petición por vista (plan v4.2 §8b.3). Se provee en `app-ciec-analysis-view`, así que vive lo que vive la vista y
 * cada ficha o arrendamiento tiene la suya.
 *
 * `null` en el flujo = cargando. La petición sale cuando la primera pestaña se abre (`watch`), no al pintar la ficha.
 */
@Injectable()
export class RiskScorecardStore implements OnDestroy {
  private readonly api = inject(ApiRiskScorecardService)
  private readonly response$ = new BehaviorSubject<SnapshotResponse<RiskScorecardView> | null>(null)
  private key: string | null = null
  private request: Subscription | null = null

  /** La respuesta del contexto; la pide la primera vez que alguien la mira (o si cambió el contexto). */
  watch(ctx: SnapshotContext): Observable<SnapshotResponse<RiskScorecardView> | null> {
    if (keyOf(ctx) !== this.key) this.fetch(ctx)
    return this.response$.asObservable()
  }

  /** Vuelve a pedirla: después de guardar una captura, o al reintentar tras un error. */
  reload(ctx: SnapshotContext): void {
    this.fetch(ctx)
  }

  /** Cambió el análisis de la ficha: si alguna pestaña ya la pidió, se pide de nuevo; si no, se espera a que abran una. */
  invalidate(ctx: SnapshotContext | null): void {
    if (this.key === null) return
    if (ctx) this.fetch(ctx)
    else {
      this.key = null
      this.request?.unsubscribe()
      this.response$.next(null)
    }
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe()
    this.response$.complete()
  }

  private fetch(ctx: SnapshotContext): void {
    this.key = keyOf(ctx)
    this.request?.unsubscribe()
    this.response$.next(null)
    this.request = this.api.get(ctx).subscribe((response) => this.response$.next(response))
  }
}

const keyOf = (ctx: SnapshotContext): string => `${ctx.scope}:${ctx.id}`
